import { useRef, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ListingCard } from "../../../api/catalogue";
import { Icon } from "../../ui";
import { ProductCard } from "../ProductCard";
import { SectionHeading } from "./SectionHeading";

export interface FeaturedRowProps {
  listings: ListingCard[];
  /** Each card's add-to-basket control. */
  footer: (listing: ListingCard) => ReactNode;
}

/**
 * A row of part cards that scrolls sideways (desktop kit BestSellers). Which parts belong here
 * is KALI-32; until then the route passes the newest parts in stock, and the heading says so.
 */
export function FeaturedRow({ listings, footer }: FeaturedRowProps) {
  const row = useRef<HTMLUListElement>(null);
  const scroll = (direction: 1 | -1) => row.current?.scrollBy({ left: direction * row.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <section>
      <SectionHeading
        eyebrow="Just added"
        title="New on the shelf"
        action={
          <span className="hidden gap-2 md:flex">
            <ArrowButton label="Earlier parts" onClick={() => scroll(-1)}>
              <Icon icon={ChevronLeft} size={18} />
            </ArrowButton>
            <ArrowButton label="More parts" onClick={() => scroll(1)}>
              <Icon icon={ChevronRight} size={18} />
            </ArrowButton>
          </span>
        }
      />
      <ul ref={row} className="m-0 -mx-4 flex list-none snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:scroll-px-6 sm:px-6 lg:gap-6">
        {listings.map((listing) => (
          <li key={listing.id} className="flex w-[200px] flex-none snap-start sm:w-[220px]">
            <ProductCard listing={listing} footer={footer(listing)} className="w-full" />
          </li>
        ))}
      </ul>
    </section>
  );
}

function ArrowButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-10 cursor-pointer items-center justify-center rounded-sm border border-border-default bg-surface-card text-ink-700 hover:border-green-700"
    >
      {children}
    </button>
  );
}
